'use client'

import { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react'
import { useStore } from '@tanstack/react-store'
import { useRouter } from '@tanstack/react-router'
import { getSessionUser, logoutAction } from '#/lib/auth-actions'
import { publishChanges, getGitHubUploadConfig } from '#/lib/git-actions'
import { LARGEST_SUFFIX, pathWithWidth, stemFromLargest } from '#/lib/image-paths'
import { processImageInBrowser } from '#/lib/image-processing-browser'
import { getBuildSha } from '#/lib/build-id'
import { cmsStore, getUnpublishedCount, getPendingEditCount, markPublished, clearPendingEdits, hydrateFromStorage } from '#/lib/cms-store'
import { Loader2, ChevronUp } from 'lucide-react'
import PublishPanel from './PublishPanel'

interface CmsUser {
  user: string
  avatarUrl: string | undefined
}

interface CmsContextValue {
  isEditMode: boolean
  setEditMode: (v: boolean) => void
  isAuthenticated: boolean
  user: CmsUser | null
  isLoading: boolean
  login: () => void
  logout: () => void
  unpublishedCount: number
  isPublished: boolean
  isPublishing: boolean
  publish: () => Promise<void>
}

const CmsContext = createContext<CmsContextValue>({
  isEditMode: false,
  setEditMode: () => {},
  isAuthenticated: false,
  user: null,
  isLoading: true,
  login: () => {},
  logout: () => {},
  unpublishedCount: 0,
  isPublished: false,
  isPublishing: false,
  publish: async () => {},
})

export function useCms() {
  return useContext(CmsContext)
}

export function CmsProvider({ children }: { children: React.ReactNode }) {
  // Start in the same state on both server and client (false) to avoid hydration
  // mismatch, then restore from sessionStorage after mount.
  const [mounted, setMounted] = useState(false)
  const [isEditMode, _setEditMode] = useState(false)
  useEffect(() => {
    setMounted(true)
    hydrateFromStorage()
    if (sessionStorage.getItem('msb-cms-edit-mode') === 'true') {
      _setEditMode(true)
    }
  }, [])
  const setEditMode = useCallback((value: boolean) => {
    _setEditMode(value)
    if (typeof window !== 'undefined') {
      if (value) {
        sessionStorage.setItem('msb-cms-edit-mode', 'true')
      } else {
        sessionStorage.removeItem('msb-cms-edit-mode')
      }
    }
  }, [])
  const [user, setUser] = useState<CmsUser | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isPublishing, setIsPublishing] = useState(false)
  const [publishFeedback, setPublishFeedback] = useState<{
    type: 'success' | 'error'
    message: string
  } | null>(null)
  const router = useRouter()

  const isAuthenticated = !!user

  // Read pending edit count from the store
  const unpublishedCount = useStore(cmsStore, (s) => getUnpublishedCount(s))
  const isPublished = useStore(cmsStore, (s) => s.published)
  const [deployStatus, setDeployStatus] = useState<'idle' | 'deploying' | 'live'>('idle')
  const [isPanelOpen, setIsPanelOpen] = useState(false)
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null)

  // Auto-close panel when no changes remain
  useEffect(() => {
    if (unpublishedCount === 0) setIsPanelOpen(false)
  }, [unpublishedCount])

  const publish = useCallback(async () => {
    const state = cmsStore.state
    if (getPendingEditCount(state) === 0) return

    setIsPublishing(true)
    setPublishFeedback(null)
    try {
      // Process pending images into 4 WebP variants + LQIP entirely in the browser
      // (avoids sending raw multi-MB payloads through the Vercel function), then
      // upload each variant directly to GitHub as a blob.
      const imageShas: Record<string, string> = {}
      const imageLqips: Record<string, string> = {}
      if (Object.keys(state.pendingImages).length > 0) {
        const config = await getGitHubUploadConfig()
        for (const [imgPath, dataUrl] of Object.entries(state.pendingImages)) {
          // imgPath is the canonical largest-variant path, e.g. ".../foo-1800.webp"
          if (!imgPath.endsWith(LARGEST_SUFFIX)) continue
          const processed = await processImageInBrowser(dataUrl)
          const stem = stemFromLargest(imgPath)
          for (const variant of processed.variants) {
            const variantPath = pathWithWidth(stem, variant.width)
            const res = await fetch(`https://api.github.com/repos/${config.owner}/${config.repo}/git/blobs`, {
              method: 'POST',
              headers: {
                Authorization: `Bearer ${config.token}`,
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({ content: variant.base64, encoding: 'base64' }),
            })
            const blob = await res.json()
            imageShas[variantPath] = blob.sha
          }
          imageLqips[imgPath] = processed.lqip
        }
      }

      const result = await publishChanges({
        data: {
          edits: state.pendingEdits,
          imageShas,
          imageLqips,
        },
      })
      if (result.success && 'sha' in result) {
        markPublished()
        setPublishFeedback({ type: 'success', message: 'Published!' })

        // Check if we're in local dev (no Vercel build SHA)
        const { sha: buildSha } = await getBuildSha()
        if (buildSha === 'dev') {
          // Local dev: git fetch + checkout already synced files in publishChanges
          clearPendingEdits()
          router.invalidate()
          setDeployStatus('live')
          setTimeout(() => setDeployStatus('idle'), 5000)
        } else {
          // Production: poll until the deployed SHA matches the new commit
          setDeployStatus('deploying')
          const targetSha = result.sha as string
          if (pollRef.current) clearInterval(pollRef.current)
          pollRef.current = setInterval(async () => {
            try {
              const { sha } = await getBuildSha()
              if (sha === targetSha) {
                if (pollRef.current) clearInterval(pollRef.current)
                pollRef.current = null
                clearPendingEdits()
                router.invalidate()
                setDeployStatus('live')
                setTimeout(() => setDeployStatus('idle'), 5000)
              }
            } catch {
              // Network blip — keep polling
            }
          }, 10_000)
        }
      } else if (!result.success) {
        setPublishFeedback({ type: 'error', message: result.error ?? 'Publish failed' })
      }
    } catch {
      setPublishFeedback({ type: 'error', message: 'Publish failed. Please try again.' })
    } finally {
      setIsPublishing(false)
      setTimeout(() => setPublishFeedback(null), 4000)
    }
  }, [])

  // Clean up polling on unmount
  useEffect(() => {
    return () => {
      if (pollRef.current) clearInterval(pollRef.current)
    }
  }, [])

  const login = useCallback(() => {
    const clientId = import.meta.env.VITE_GITHUB_CLIENT_ID
    if (!clientId) {
      console.error('VITE_GITHUB_CLIENT_ID is not set')
      return
    }
    const redirectUri = `${window.location.origin}/api/auth/callback`
    window.location.href = `https://github.com/login/oauth/authorize?client_id=${clientId}&redirect_uri=${encodeURIComponent(redirectUri)}&scope=read:user`
  }, [])

  const logout = useCallback(async () => {
    await logoutAction()
    setUser(null)
    setEditMode(false)
  }, [])

  // Check session on mount
  useEffect(() => {
    getSessionUser()
      .then((sessionUser) => {
        if (sessionUser) {
          setUser(sessionUser)
        }
      })
      .catch(() => {
        // Not authenticated
      })
      .finally(() => {
        setIsLoading(false)
      })
  }, [])

  // Handle edit mode activation
  useEffect(() => {
    if (typeof window === 'undefined') return

    // Enable via URL param
    const params = new URLSearchParams(window.location.search)
    if (params.get('edit') === 'true') {
      if (isLoading) return // Wait for auth check
      if (isAuthenticated) {
        setEditMode(true)
        // Clean up URL param
        const url = new URL(window.location.href)
        url.searchParams.delete('edit')
        window.history.replaceState({}, '', url.toString())
      } else {
        login()
      }
    }

    // Clean up auth error param display
    if (params.get('auth_error')) {
      const error = params.get('auth_error')
      console.warn(`Auth error: ${error}`)
      const url = new URL(window.location.href)
      url.searchParams.delete('auth_error')
      window.history.replaceState({}, '', url.toString())
    }
  }, [isLoading, isAuthenticated, login])

  // Toggle via keyboard shortcut: Ctrl+Shift+E
  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if (e.ctrlKey && e.shiftKey && e.key === 'E') {
        e.preventDefault()
        if (isEditMode) {
          setEditMode(false)
        } else if (isAuthenticated) {
          setEditMode(true)
        } else {
          login()
        }
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [isEditMode, isAuthenticated, login])

  return (
    <CmsContext.Provider
      value={{
        isEditMode,
        setEditMode,
        isAuthenticated,
        user,
        isLoading,
        login,
        logout,
        unpublishedCount,
        isPublished,
        isPublishing,
        publish,
      }}
    >
      {children}
      {mounted && isEditMode && (
        <div className="fixed bottom-4 left-4 z-[9999] flex items-center gap-2 rounded-full bg-primary px-3 py-1.5 text-xs font-semibold shadow-lg text-white">
          <span>Edit Mode</span>
          {user && (
            <>
              <span className="opacity-60">|</span>
              <img
                src={user.avatarUrl}
                alt={user.user}
                className="h-5 w-5 rounded-full"
              />
              <span>{user.user}</span>
            </>
          )}

          {/* Publish section */}
          {unpublishedCount > 0 && (
            <>
              <span className="opacity-60">|</span>
              <button
                onClick={() => setIsPanelOpen((v) => !v)}
                className="flex items-center gap-1 tabular-nums transition-colors hover:text-white/80"
              >
                {unpublishedCount} {unpublishedCount === 1 ? 'change' : 'changes'}
                <ChevronUp
                  className={`size-3 transition-transform ${isPanelOpen ? 'rotate-180' : ''}`}
                />
              </button>
              <button
                onClick={publish}
                disabled={isPublishing}
                className="ml-0.5 rounded-full bg-white/20 px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide transition-colors hover:bg-white/30 disabled:opacity-60"
              >
                {isPublishing ? (
                  <span className="flex items-center gap-1">
                    <Loader2 className="size-3 animate-spin" />
                    Publishing
                  </span>
                ) : (
                  'Publish'
                )}
              </button>
            </>
          )}

          {/* Deploy status indicator */}
          {deployStatus === 'deploying' && (
            <>
              <span className="opacity-60">|</span>
              <span className="flex items-center gap-1 text-green-200">
                <Loader2 className="size-3 animate-spin" />
                Deploying
              </span>
            </>
          )}
          {deployStatus === 'live' && (
            <>
              <span className="opacity-60">|</span>
              <span className="text-green-200">Changes are live!</span>
            </>
          )}

          {/* Feedback toast */}
          {publishFeedback && (
            <>
              <span className="opacity-60">|</span>
              <span
                className={
                  publishFeedback.type === 'success'
                    ? 'text-green-200'
                    : 'text-red-200'
                }
              >
                {publishFeedback.message}
              </span>
            </>
          )}

          <span className="opacity-60">|</span>
          <button
            onClick={logout}
            className="rounded px-1.5 py-0.5 text-[10px] hover:bg-white/20 transition-colors"
          >
            Logout
          </button>
        </div>
      )}
      {mounted && isEditMode && (
        <PublishPanel
          open={isPanelOpen}
          onClose={() => setIsPanelOpen(false)}
          onPublish={publish}
          isPublishing={isPublishing}
        />
      )}
    </CmsContext.Provider>
  )
}
